import { BASE, LIGHT } from '../../../palette';
import { pt, type Pt } from '../geom';
import { cornerMarks, plateSigil, weaveField } from '../marks';
import { label, solid, type FaceSpec } from '../spec';
import { BACK } from '../tune';

/**
 * **牌背。**
 *
 * 一张牌的**反面**，是这台机器连续印的那面纸 —— 所以它不带任何一张牌的信息，
 * 十三种牌共用同一个画面。
 *
 * ## 为什么零酸色（这条是功能，不是风格）
 *
 * 桌上任何一处出现酸色，就说明**那里有一张正面朝上的牌**。
 * 牌背一旦沾了酸色，这个色码就不成立了 —— 玩家会开始怀疑"背面朝上的这张是什么"。
 * 这和五张无效牌零酸色是同一个手法，但理由更硬：那边是"视觉静音"，
 * 这边是**信息**。
 *
 * ## 画的是什么
 *
 * 一张过版纸（makeready sheet）—— 正式开印之前，印在废纸上对套准的那一张。
 * 它上面必然有：交叉的网点（对网）、裁切角标、以及**上一次印歪了的残影**。
 * 中央那个标记就是那个残影：同一张牌印了两遍，第二遍没对准。
 *
 * ⚠️ 织纹用的是 `hatch.ts` 里和**危险条纹同一个函数**。
 * 牌背的布纹、异常牌底部的危险条、濒死横带 —— 全套只有这一处写出条纹几何。
 */
export function backFace(): FaceSpec {
  // 织纹要盖满整张牌，还要往外一点：它是满版的，裁边不该看到"图案到头了"
  const field: readonly Pt[] = [pt(-8, -8), pt(108, -8), pt(108, 148), pt(-8, 148)];

  const weave = weaveField(field, {
    linePitch: BACK.linePitch,
    lineAngle: BACK.lineAngle,
    lineDuty: BACK.lineDuty,
    lineAlpha: BACK.lineAlpha,
    dotPitch: BACK.dotPitch,
    dotAngle: BACK.dotAngle,
    dotCoverage: BACK.dotCoverage,
    dotAlpha: BACK.dotAlpha,
  });

  const marks = cornerMarks(100, 140, BACK.markInset, BACK.markArm, LIGHT.base, 0.55, 0.9);

  const sigil = plateSigil({
    x: 50,
    y: 70,
    w: BACK.plateW,
    h: BACK.plateH,
    ghostX: BACK.ghostX,
    ghostY: BACK.ghostY,
    stroke: 1.7,
  });

  return {
    ground: solid(BASE.void),
    accent: LIGHT.base,
    border: { width: 1, color: LIGHT.base, alpha: 0.28 },
    // ⚠️ 层序是 底色 → spill → prims → 外框。织纹进 spill，标记进 prims，
    //    所以标记一定压在织纹上面 —— 不必靠数组顺序去保证
    spill: [...weave, ...marks],
    prims: [
      ...sigil,
      // ⚠️ 这里原来是**两段**（左 ACID SAMIZDAT / 右 MAKEREADY 00），
      //    结果在 100 宽的牌上撞在一起印成「ACID SAMIZMAKEREADY 00」。
      //    牌只有 100 单位宽，两段加起来的字宽是放不下的 —— 改成一整行居中。
      label('ACID SAMIZDAT · MAKEREADY 00', 50, 131, {
        size: 2.6,
        color: LIGHT.base,
        alpha: 0.5,
        tracking: 0.7,
        anchor: 'middle',
      }),
    ],
    // ⚠️ 牌背是**这台机器自己印的**，所以它要印刷层。
    //    但目前 `press` 还没有消费者（印刷层没实现），这里只是把意图写对
    press: true,
  };
}
